import React from "react";
import styled from "styled-components";
import aa from "search-insights";
import {logEvent} from "@/utils/analytics";

const tempImageFix = url => {
  if (!url) return "https://cdn.hackernoon.com/fallback-feat.png";

  if (!url.startsWith("http")) {
    url = new URL(url, "https://hackernoon.com/").toString();
  }

  url = url.replace("https://hackernoon.com/", "https://cdn.hackernoon.com/");

  return url;
};

const StyledLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  position: absolute;
  right: 0;
  bottom: 0;
  background-color: #0f0;
  color: #1a1a1a;
  padding: 0 0.5rem;
  // border-radius: 4px;
  text-transform: uppercase;
`;

const StoryHitContainer = styled.div`
  display: flex;
  color: #cfcfcf;
  border-bottom: 1px solid #0f0;
  padding: 0.5rem 0;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  cursor: pointer;
  position: relative;
`;

const StoryContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StoryHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const StoryTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 1rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  &:hover {
    color: #e0e0e0;
    text-decoration: underline;
  }
`;

const StoryImageWrapper = styled.div`
  display: flex;
`;

const StoryImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: cover;
  transition: transform 0.2s;
  z-index: 1;

  &:hover {
    transform: scale(1.05);
  }
`;

const StoryDetails = styled.div`
  flex: 1;
  position: relative;
  padding-left: 1rem;
  border-radius: 0 0 4px 4px;
  width: 100%;
`;

const StoryDescription = styled.p`
  font-size: 0.75rem;
`;

const AuthorSection = styled.div`
  margin-top: 0.5rem;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  z-index: 1;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const AuthorHandle = styled.span`
  margin-left: 0.5rem;
  font-size: 0.75rem;
  font-weight: bold;

  .light {
    font-weight: 300;
  }
`;

const ProfileHitContainer = styled.div`
  position: relative;
  display: flex;
  padding: 0.5rem 0;
  color: #cfcfcf;
  transition: transform 0.2s;
  cursor: pointer;
  border-bottom: 1px solid #0f0;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 75px;
  height: 75px;
  border-radius: 4px;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const ProfileInfo = styled.div`
  margin-left: 0.5rem;
`;

const ProfileNameContainer = styled.div`
  display: flex;
  flex-direction: ${props => (props.bioLength > 40 ? "row" : "column")};
  align-items: ${props => (props.bioLength > 40 ? "center" : "flex-start")};
`;

const ProfileDisplayName = styled.span`
  font-weight: bold;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ProfileHandle = styled.span`
  font-size: 0.75rem;
  margin-left: ${props => (props.bioLength > 40 ? "0.5rem" : "0")};
`;

const ProfileBio = styled.p`
  font-size: 0.75rem;
`;

const TagHitContainer = styled.div`
  position: relative;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 0;
  color: #cfcfcf;
  transition: transform 0.2s;
  cursor: pointer;
  border-bottom: 1px solid #0f0;
`;

const TagImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 75px;
  height: 75px;
  border-radius: 4px;
`;

const TagImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const TagInfo = styled.div`
  flex: 2;
`;

const TagTitle = styled.span`
  font-weight: bold;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const TagCategory = styled.p`
  font-size: 0.75rem;
`;

const TagDescription = styled.p`
  font-size: 0.75rem;
`;

const CompanyHitContainer = styled.div`
  position: relative;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 0;
  cursor: pointer;
  color: #cfcfcf;
  transition: transform 0.2s;
  border-bottom: 1px solid #0f0;
`;

const CompanyLogoWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 75px;
  height: 75px;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const CompanyInfo = styled.div`
  flex: 2;
`;

const CompanyNameContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
  flex-direction: ${props => (props.descLength > 40 ? "row" : "column")};
  align-items: ${props => (props.descLength > 40 ? "center" : "flex-start")};
`;

const CompanyName = styled.span`
  font-weight: bold;
  font-size: 0.875rem;
  margin-right: 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

const CompanyUrl = styled.span`
  font-size: 0.75rem;
`;

const CompanyDescription = styled.p`
  font-size: 0.75rem;
`;

const SearchSectionHeader = styled.div`
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  padding: 15px;
`;

const Label = ({label}) => <StyledLabel>{label?.toUpperCase()}</StyledLabel>;

const HitComponents = {
  stories: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "blog";

    const getDescription = () => {
      if (hit?.extra?.description?.length > 50) {
        return hit?.image ? `${hit.extra.description.slice(0, 90)}...` : `${hit.extra.description.slice(0, 120)}...`;
      }
      return hit?.extra?.description;
    };

    return (
      <StoryHitContainer onClick={() => clickSearchResult && clickSearchResult(`/${hit?.slug}`, hit, label, "public")}>
        <Label label={label} />
        <StoryContent>
          <StoryHeader>
            <StoryTitle>{hit?.title}</StoryTitle>
          </StoryHeader>

          <StoryImageWrapper>
            <div style={{position: "relative", width: "120px", height: "85px"}}>
              <StoryImage src={tempImageFix(hit?.image, 100)} alt={hit?.title || "Story image"} loading="lazy" />
            </div>

            <StoryDetails>
              <StoryDescription>{getDescription()}</StoryDescription>

              <AuthorSection>
                <AuthorInfo>
                  <AuthorImage
                    src={tempImageFix(hit?.extra?.profile.avatar, 30)}
                    alt={`${hit?.extra?.profile.handle} profile image`}
                    loading="lazy"
                  />
                  <AuthorHandle>
                    <span className="light">by</span> @{hit?.extra?.profile.handle}
                  </AuthorHandle>
                </AuthorInfo>
              </AuthorSection>
            </StoryDetails>
          </StoryImageWrapper>
        </StoryContent>
      </StoryHitContainer>
    );
  },

  profiles: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "people";
    const bioLength = hit?.extra?.bio?.length || 0;

    return (
      <ProfileHitContainer
        onClick={() => clickSearchResult && clickSearchResult(`/${hit?.slug}`, hit, label, "public")}
      >
        <Label label={label} />
        <ProfileImageWrapper>
          <ProfileImage alt={`${hit?.slug} feature image`} src={tempImageFix(hit?.image || "/fallback-profile.png")} />
        </ProfileImageWrapper>

        <ProfileInfo>
          {hit?.extra?.displayName?.length > 3 ? (
            <ProfileNameContainer bioLength={bioLength}>
              <ProfileDisplayName>{hit?.extra?.displayName}</ProfileDisplayName>
              <ProfileHandle bioLength={bioLength}>@{hit?.title}</ProfileHandle>
            </ProfileNameContainer>
          ) : (
            <div>
              <ProfileDisplayName>@{hit?.title}</ProfileDisplayName>
            </div>
          )}
          {hit?.extra?.bio?.length > 3 && <ProfileBio>{hit?.extra?.bio}</ProfileBio>}
        </ProfileInfo>
      </ProfileHitContainer>
    );
  },

  tags: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "topic";

    const getDescription = () => {
      if (hit?.extra?.description?.length > 50) {
        return hit?.image ? `${hit.extra.description.slice(0, 90)}...` : `${hit.extra.description.slice(0, 120)}...`;
      }
      return hit?.extra?.description;
    };

    return (
      <TagHitContainer onClick={() => clickSearchResult && clickSearchResult(`/${hit?.slug}`, hit, label, "public")}>
        <Label label={label} />
        <TagImageWrapper>
          <TagImage alt={`${hit?.slug} feature image`} src={tempImageFix(hit?.image || "/fallback-profile.png")} />
        </TagImageWrapper>

        <TagInfo>
          {hit?.extra?.title?.length > 3 ? (
            <div>
              <TagTitle>#{hit?.extra?.title}</TagTitle>
            </div>
          ) : (
            <div>
              <TagTitle>#{hit?.title}</TagTitle>
            </div>
          )}
          {hit?.extra?.parentCategory && <TagCategory>{hit?.extra?.parentCategory}</TagCategory>}
          <TagDescription>{getDescription()}</TagDescription>
        </TagInfo>
      </TagHitContainer>
    );
  },

  companies: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "company";
    const descLength = hit?.extra?.description?.length || 0;

    const getDescription = () => {
      if (hit?.extra?.description?.length > 50) {
        return hit?.image ? `${hit.extra.description.slice(0, 90)}...` : `${hit.extra.description.slice(0, 120)}...`;
      }
      return hit?.extra?.description;
    };

    return (
      <CompanyHitContainer
        onClick={() => clickSearchResult && clickSearchResult(`/${hit?.slug}`, hit, label, "public")}
      >
        <Label label={label} />
        <CompanyLogoWrapper>
          <CompanyLogo
            alt={`${hit?.title} logo`}
            src={tempImageFix(hit?.image || "https://hackernoon.imgix.net/company-avatar-fallback.png")}
            onError={e => {
              e.target.onerror = null;
              e.target.src = "https://hackernoon.imgix.net/company-avatar-fallback.png";
            }}
          />
        </CompanyLogoWrapper>

        <CompanyInfo>
          {hit?.title?.length > 3 ? (
            <CompanyNameContainer descLength={descLength}>
              <CompanyName>{hit?.title}</CompanyName>
              {hit?.url && <CompanyUrl>{hit.url}</CompanyUrl>}
            </CompanyNameContainer>
          ) : (
            <div>
              <CompanyName>{hit?.title}</CompanyName>
            </div>
          )}
          <CompanyDescription>{getDescription()}</CompanyDescription>
        </CompanyInfo>
      </CompanyHitContainer>
    );
  },

  coins: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "coin";
    const bioLength = hit?.extra?.bio?.length || 0;

    const getDescription = () => {
      if (hit?.extra?.description?.length > 50) {
        return hit?.image ? `${hit.extra.description.slice(0, 90)}...` : `${hit.extra.description.slice(0, 120)}...`;
      }
      return hit?.extra?.description;
    };

    return (
      <TagHitContainer onClick={() => clickSearchResult && clickSearchResult(`/${hit?.slug}`, hit, label, "public")}>
        <Label label={label} />
        <TagImageWrapper>
          <TagImage alt={`/u/${hit?.slug} feature image`} src={tempImageFix(hit?.image || "/fallback-profile.png")} />
        </TagImageWrapper>

        <TagInfo>
          {hit?.extra?.displayName?.length > 3 ? (
            <ProfileNameContainer bioLength={bioLength}>
              <ProfileDisplayName>${hit?.extra?.displayName}</ProfileDisplayName>
              <ProfileHandle bioLength={bioLength}>@{hit?.title}</ProfileHandle>
            </ProfileNameContainer>
          ) : (
            <div>
              <ProfileDisplayName>${hit?.title}</ProfileDisplayName>
            </div>
          )}
          <TagDescription>{getDescription()}</TagDescription>
        </TagInfo>
      </TagHitContainer>
    );
  },

  pages: ({hit = null, clickSearchResult}) => {
    if (!hit) return null;
    const label = "site";
    const descLength = hit?.extra?.description?.length || 0;

    const getDescription = () => {
      if (hit?.extra?.description?.length > 50) {
        return hit?.image ? `${hit.extra.description.slice(0, 90)}...` : `${hit.extra.description.slice(0, 120)}...`;
      }
      return hit?.extra?.description;
    };

    return (
      <CompanyHitContainer onClick={() => clickSearchResult && clickSearchResult(`${hit?.slug}`, hit, label, "public")}>
        <Label label={label} />
        <CompanyLogoWrapper>
          <CompanyLogo
            alt={`${hit?.title} logo`}
            src={tempImageFix(hit?.image || "https://hackernoon.imgix.net/company-avatar-fallback.png")}
          />
        </CompanyLogoWrapper>

        <CompanyInfo>
          {hit?.title?.length > 3 ? (
            <CompanyNameContainer descLength={descLength}>
              <CompanyName>{hit?.title}</CompanyName>
              {hit?.url && <CompanyUrl>{hit.url}</CompanyUrl>}
            </CompanyNameContainer>
          ) : (
            <div>
              <CompanyName>{hit?.title}</CompanyName>
            </div>
          )}
          <CompanyDescription>{getDescription()}</CompanyDescription>
        </CompanyInfo>
      </CompanyHitContainer>
    );
  },
};

const GenericHits = ({hits, user}) => {
  const analytics = event => {
    logEvent("Search on header", event);
  };

  const clickSearchResult = (route, hit, eventName, indexName) => {
    if (user && user.uid && user?.trackingOptIn === true) {
      aa("clickedObjectIDsAfterSearch", {
        userToken: user.uid,
        eventName: "clicked_" + eventName,
        index: indexName,
        queryID: hit.__queryID,
        objectIDs: [hit.objectID],
        positions: [hit.__position],
      });
    }
    analytics("search result");
    if (eventName === "site") window.open(route, "_target");
    else if (route) window.open("https://hackernoon.com" + route, "_target");
  };

  if (!hits.length) return null;

  return (
    <SearchSectionHeader>
      {hits.map(hit => (
        <React.Fragment key={hit?.objectID}>
          {hit?.type === "stories" && <HitComponents.stories hit={hit} clickSearchResult={clickSearchResult} />}
          {hit?.type === "profiles" && <HitComponents.profiles hit={hit} clickSearchResult={clickSearchResult} />}
          {hit?.type === "companies" && <HitComponents.companies hit={hit} clickSearchResult={clickSearchResult} />}
          {hit?.type === "tags" && <HitComponents.tags hit={hit} clickSearchResult={clickSearchResult} />}
          {hit?.type === "coins" && <HitComponents.coins hit={hit} clickSearchResult={clickSearchResult} />}
          {hit?.type === "page" && <HitComponents.pages hit={hit} clickSearchResult={clickSearchResult} />}
        </React.Fragment>
      ))}
    </SearchSectionHeader>
  );
};

export default GenericHits;
